import { getCookie, setCookie } from 'cookies-next'

/**
 * Marketing attribution (HUNCH-6674), kept in a first-party cookie for 30 days and sent with sign-up:
 * - first touch: the visit that created the cookie, never changed afterwards;
 * - last touch: overwritten by every later visit that carries campaign parameters;
 * - click ids (gclid/fbclid) and the referral code keep the last one seen.
 */
export interface UserAttribution {
  first_utm_source: string | null
  first_utm_medium: string | null
  first_utm_campaign: string | null
  first_utm_content: string | null
  first_utm_term: string | null
  last_utm_source: string | null
  last_utm_medium: string | null
  last_utm_campaign: string | null
  last_utm_content: string | null
  last_utm_term: string | null
  gclid: string | null
  fbclid: string | null
  referrer: string | null
  landing_page: string | null
  first_visit_at: string | null
  referral_code: string | null
}

export const ATTRIBUTION_COOKIE = 'tbl_attr'
export const ATTRIBUTION_TTL_SECONDS = 60 * 60 * 24 * 30 // 30 days

// Any of these marks a new campaign visit, which replaces the last touch.
const CAMPAIGN_PARAMS = ['utm_source', 'utm_medium', 'gclid', 'fbclid']
// Same limits as the backend columns; also keeps the cookie well under the browser's 4KB.
const MAX_LENGTH = 255

const clean = (value: string | null | undefined, lowercase = false): string | null => {
  const text = (value ?? '').trim().slice(0, MAX_LENGTH)
  if (!text) return null
  return lowercase ? text.toLowerCase() : text
}

// The referring site without its query string or fragment: shorter, and drops search terms or tokens.
const cleanReferrer = (referrer: string): string | null => {
  if (!referrer) return null
  try {
    const url = new URL(referrer)
    return clean(`${url.origin}${url.pathname}`)
  } catch {
    return null
  }
}

type FirstTouchUtm = Pick<
  UserAttribution,
  | 'first_utm_source'
  | 'first_utm_medium'
  | 'first_utm_campaign'
  | 'first_utm_content'
  | 'first_utm_term'
>
type LastTouchUtm = Pick<
  UserAttribution,
  'last_utm_source' | 'last_utm_medium' | 'last_utm_campaign' | 'last_utm_content' | 'last_utm_term'
>

// Source, medium and campaign are lowercased so "Meta" and "meta" report as one channel.
const firstTouchUtm = (params: URLSearchParams): FirstTouchUtm => ({
  first_utm_source: clean(params.get('utm_source'), true),
  first_utm_medium: clean(params.get('utm_medium'), true),
  first_utm_campaign: clean(params.get('utm_campaign'), true),
  first_utm_content: clean(params.get('utm_content')),
  first_utm_term: clean(params.get('utm_term')),
})

const lastTouchUtm = (params: URLSearchParams): LastTouchUtm => ({
  last_utm_source: clean(params.get('utm_source'), true),
  last_utm_medium: clean(params.get('utm_medium'), true),
  last_utm_campaign: clean(params.get('utm_campaign'), true),
  last_utm_content: clean(params.get('utm_content')),
  last_utm_term: clean(params.get('utm_term')),
})

const referralCode = (params: URLSearchParams) =>
  clean(params.get('ref')) ?? clean(params.get('referral_code'))

export interface Visit {
  search: string
  pathname: string
  referrer: string
  now: Date
}

/**
 * The attribution after this visit, or null when the cookie doesn't need to change (a later visit without
 * campaign parameters or a referral code), so its 30 days aren't restarted by ordinary browsing.
 */
export const nextAttribution = (
  current: UserAttribution | null,
  { search, pathname, referrer, now }: Visit,
): UserAttribution | null => {
  const params = new URLSearchParams(search)

  if (!current) {
    return {
      ...firstTouchUtm(params),
      ...lastTouchUtm(params),
      gclid: clean(params.get('gclid')),
      fbclid: clean(params.get('fbclid')),
      referrer: cleanReferrer(referrer),
      landing_page: clean(pathname),
      first_visit_at: now.toISOString(),
      referral_code: referralCode(params),
    }
  }

  const isCampaignVisit = CAMPAIGN_PARAMS.some((param) => clean(params.get(param)))
  const newReferralCode = referralCode(params)
  if (!isCampaignVisit && !newReferralCode) return null

  return {
    ...current,
    ...(isCampaignVisit && {
      ...lastTouchUtm(params),
      // Keep the last click id seen: a later campaign visit without one doesn't erase an earlier ad click.
      gclid: clean(params.get('gclid')) ?? current.gclid,
      fbclid: clean(params.get('fbclid')) ?? current.fbclid,
    }),
    referral_code: newReferralCode ?? current.referral_code,
  }
}

export const getAttribution = (): UserAttribution | null => {
  const value = getCookie(ATTRIBUTION_COOKIE)
  if (typeof value !== 'string' || !value) return null
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as UserAttribution)
      : null
  } catch {
    return null
  }
}

const setAttribution = (attribution: UserAttribution) => {
  setCookie(ATTRIBUTION_COOKIE, JSON.stringify(attribution), {
    maxAge: ATTRIBUTION_TTL_SECONDS,
    path: '/',
    sameSite: 'lax',
    secure: process.env.NODE_ENV === 'production',
  })
}

/** Records this page visit in the attribution cookie. Browser only. */
export const captureAttribution = () => {
  const next = nextAttribution(getAttribution(), {
    search: window.location.search,
    pathname: window.location.pathname,
    referrer: document.referrer,
    now: new Date(),
  })
  if (next) setAttribution(next)
}
